/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams } from "react-router-dom";
import { InfoTable } from "../../../common/components/ui/table/InfoTable";
import { useEffect, useMemo, useState } from "react";
import {
  ITrafficFine,
  TRAFFICFINE_STATUS,
} from "../../../types/trafficfine.type";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  chargeTrafficFine,
  getDetailsTrafficFines,
  handleFileChangeUpdate,
  TrafficFinesChangeUpdateFile,
  updateTrafficFine,
} from "../../../common/services/trafficfines.api";
import { numberToCurrency } from "../../../common/helpers/currency.helpers";
import Loading from "../../../common/components/ui/loading/Loading";
import { useNavigate } from "react-router-dom";
import { ICharge, CHARGE_STATUS } from "../../../types/charge.type";
import { formatDate } from "../../../common/helpers/date.helpers";
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import * as pdfjsLib from "pdfjs-dist";
import { sendFile } from "../../../common/services/hold-document";
import { retrieveVehicleLicensePlate } from "../../../common/services/vehicles.api";
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

import { getRenters } from "../../../common/services/renters.api";
import AsyncSelect from "react-select/async";

import { PiNotePencilLight } from "react-icons/pi";
import { Button } from "@/components/ui/button";
import { ModalViewLogs } from "@/modules/logsDetails/logsDetails.modal";

const REGEX = {
  linhaDigitavel:
    /\b(\d{5}\.\d{5})\s(\d{5}\.\d{6})\s(\d{5}\.\d{6})\s(\d)\s(\d{14})\b/g,
};

const STATUS_TRAFFICFINE = {
  [TRAFFICFINE_STATUS.PAID]: "Paga",
  [TRAFFICFINE_STATUS.OPEN]: "Aberta",
  [TRAFFICFINE_STATUS.OVERDUE]: "Vencida",
};

const STATUS_TRAFFICFINE_COLORS = {
  [TRAFFICFINE_STATUS.PAID]: "bg-greenPrimary text-white",
  [TRAFFICFINE_STATUS.OPEN]: "bg-bluePrimary text-white",
  [TRAFFICFINE_STATUS.OVERDUE]: "bg-redPrimary text-white",
};

const STATUS_CHARGE = {
  [CHARGE_STATUS.RECEIVED]: "Paga",
  [CHARGE_STATUS.PENDING]: "Pendente",
  [CHARGE_STATUS.OVERDUE]: "Vencida",
};

const STATUS_CHARGE_COLORS = {
  [CHARGE_STATUS.RECEIVED]: "bg-greenPrimary text-white",
  [CHARGE_STATUS.PENDING]: "bg-bluePrimary text-white",
  [CHARGE_STATUS.OVERDUE]: "bg-redPrimary text-white",
};

function getStatusTrafficFine(status?: TRAFFICFINE_STATUS) {
  if (!status || !(status in STATUS_TRAFFICFINE)) {
    return (
      <span className="px-2 p-1 rounded-md bg-gray-300 text-black">
        Desconhecido
      </span>
    );
  }
  return (
    <span
      className={`px-2 p-1 rounded-md ${STATUS_TRAFFICFINE_COLORS[status]}`}
    >
      {STATUS_TRAFFICFINE[status]}
    </span>
  );
}

function getStatusCharge(status?: CHARGE_STATUS) {
  if (!status || !(status in STATUS_CHARGE)) {
    return (
      <span className="px-2 p-1 rounded-md bg-gray-300 text-black">
        Desconhecido
      </span>
    );
  }
  return (
    <span className={`px-2 p-1 rounded-md ${STATUS_CHARGE_COLORS[status]}`}>
      {STATUS_CHARGE[status]}
    </span>
  );
}

function generateLinkInvoice(externalId: string) {
  const invoiceId = externalId?.split("_")[1];
  return (
    <a
      href={`https://asaas.com/i/${invoiceId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-bluePrimary underline cursor-pointer"
    >
      Visualizar
    </a>
  );
}

const formatValue = (value: any) => value ?? "-";

const TrafficFineSection = ({
  title,
  data,
  className,
}: {
  title: string;
  data: { label: string; value: any }[];
  className: string;
}) => (
  <section className={`tabs-item-style ${className}`}>
    <div>
      <InfoTable title={title} data={data} />
    </div>
  </section>
);

export default function TrafficFinesDetailsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRenter, setSelectedRenter] = useState<any>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);

  const { notifyError, notifySuccess } = useToastContext();
  const [vehicleId, setVehicleId] = useState<string>("");
  const [isDisabledSaveButton, setIsDisabledSaveButton] = useState(false);
  function openPdfSelector(onFileSelect: (file: File) => void) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";

    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        onFileSelect(target.files[0]);
      }
    };

    input.click();
  }

  const loadRenters = async (inputValue: string) => {
    try {
      const response = await getRenters({ search: inputValue });
      return response.data.map((renter: any) => ({
        label: `${renter.name} - ${renter.cpf}`,
        value: renter._id,
        ...renter,
      }));
    } catch (error) {
      notifyError("Erro ao buscar clientes");
      return [];
    }
  };

  const handleFileChange = async (file: File) => {
    setIsDisabled(true);
    setIsLoadingButton(true);

    try {
      if (!(file.size > 0)) {
        setIsDisabled(false);
        return notifyError("O arquivo selecionado está corrompido.");
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const linhasDigitaveis: string[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(" ");

        let match: RegExpExecArray | null;
        while ((match = REGEX.linhaDigitavel.exec(pageText)) !== null) {
          linhasDigitaveis.push(match[0]);
        }
      }

      if (!Array.isArray(linhasDigitaveis) || !linhasDigitaveis.length) {
        setIsDisabled(false);
        return notifyError(
          "Não foi possível localizar um código de barras no arquivo anexado."
        );
      }

      const urlResult = await handleSendFile(file);
      if (!urlResult?.length) {
        setIsDisabled(false);
        return notifyError("A URL gerada para o arquivo salvo é inválida");
      }

      const fileUpdate: TrafficFinesChangeUpdateFile = {
        _id: dataTrafficTicket?._id,
        url: urlResult,
        barcode: linhasDigitaveis[0],
      };

      const result = await handleFileChangeUpdate(fileUpdate);
      if (result.ok) {
        setIsDisabled(true);
        notifySuccess(result.message || "Arquivo enviado com sucesso!");
        return;
      }

      setIsDisabled(false);
      notifyError(result.message || `Erro ao enviar o arquivo ${file.name}`);
    } catch (error) {
      notifyError(`Erro ao enviar o arquivo ${file.name}: ${error}`);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const pathToUpload = useMemo(() => {
    return `vehicles/${vehicleId}/trafficfines`;
  }, [vehicleId]);

  async function handleSendFile(file: File): Promise<string | undefined> {
    try {
      const prefix = pathToUpload;
      const data = { prefix, file };

      const sendFileResponse = await sendFile(data);

      if (!sendFileResponse.ok)
        throw new Error(sendFileResponse.message || "Erro ao enviar o arquivo");

      const [{ key }] = sendFileResponse.data;

      return key;
    } catch (error) {
      return undefined;
    }
  }

  const navigate = useNavigate();
  const { trafficTicket } = useParams();
  const [dataTrafficTicket, setDataTrafficTicket] = useState<
    ITrafficFine | undefined
  >();
  const [dataCharge, setDataCharge] = useState<ICharge | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingGenerateCharge, setIsLoadingGenerateCharge] =
    useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  const loadTrafficFines = async () => {
    if (!trafficTicket) return;
    setIsLoading(true);
    try {
      const response = await getDetailsTrafficFines(trafficTicket);

      const resultLicensePlate = await retrieveVehicleLicensePlate(
        response.trafficFine.licensePlate
      );

      setVehicleId(resultLicensePlate.data._id);

      setDataTrafficTicket(response.trafficFine);
      setDataCharge(response.charge ?? undefined);
    } catch (error) {
      notifyError("Erro ao carregar multa");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTrafficFines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficTicket]);

  const handleGenerateCharge = async () => {
    if (!trafficTicket) return;
    setIsLoadingGenerateCharge(true);
    try {
      const response = await chargeTrafficFine(trafficTicket);
      if (response.ok) {
        notifyError("Cobrança gerada com sucesso");
        setDataCharge(response.data.charge ?? undefined);
      }
    } catch (error) {
      notifyError("Erro ao gerar cobrança");
    } finally {
      setIsLoadingGenerateCharge(false);
    }
  };

  return (
    <>
      <section className="max-w-7xl w-full p-2 felx justify-center items-center mx-auto">
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
            <div className="bg-white p-6 rounded-md w-full max-w-md">
              <h2 className="text-lg font-bold mb-4">Editar Cliente</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selecionar Cliente
                </label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadRenters}
                  onChange={(value) => setSelectedRenter(value)}
                  value={selectedRenter}
                  placeholder="Digite o nome ou CPF..."
                />
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  disabled={isDisabledSaveButton}
                  className="bg-blue-600 text-black px-4 py-2 rounded hover:bg-blue-700"
                  onClick={async () => {
                    if (!trafficTicket || !selectedRenter) return;
                    setIsDisabledSaveButton(true);
                    try {
                      const response = await updateTrafficFine(trafficTicket, {
                        renter: {
                          name: selectedRenter.name,
                          cpf: selectedRenter.cpf,
                          id: selectedRenter._id,
                        },
                      });

                      if (response.ok) {
                        notifySuccess("Cliente atualizado com sucesso!");
                        await loadTrafficFines();
                        setIsModalOpen(false);
                        setSelectedRenter(null);
                      } else {
                        notifyError("Erro ao atualizar cliente.");
                      }
                    } catch (error) {
                      notifyError("Erro ao atualizar cliente.");
                    } finally {
                      setIsDisabledSaveButton(false);
                    }
                  }}
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="tabs-border-line ">
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 mb-3 text-xl text-gray-600 hover:text-gray-800"
              >
                <span>← Voltar</span>

                <span className="">multas</span>
              </button>

              <div className="rounded-md bg-gray7">
                <div className="flex justify-end items-end">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Editar cliente"
                  >
                    <PiNotePencilLight size={20} />
                  </button>
                </div>

                <TrafficFineSection
                  title="Cliente"
                  className="mt-1"
                  data={[
                    {
                      label: "Nome",
                      value: formatValue(dataTrafficTicket?.renter?.name),
                    },
                    {
                      label: "CPF",
                      value: formatValue(dataTrafficTicket?.renter?.cpf),
                    },
                  ]}
                />
              </div>

              <TrafficFineSection
                title="Proprietário"
                className="mt-3"
                data={[
                  {
                    label: "Nome",
                    value: formatValue(dataTrafficTicket?.partner?.name),
                  },
                  {
                    label: "CNPJ",
                    value: formatValue(dataTrafficTicket?.partner?.cnpj),
                  },
                ]}
              />
              <TrafficFineSection
                title="Detalhes"
                className="mt-3"
                data={[
                  {
                    label: "Placa",
                    value: formatValue(dataTrafficTicket?.licensePlate),
                  },
                  {
                    label: "Auto de Infração",
                    value: formatValue(dataTrafficTicket?.trafficTicket),
                  },
                  {
                    label: "Data da Infração",
                    value: formatValue(dataTrafficTicket?.details?.datahora),
                  },
                  {
                    label: "Data de Vencimento",
                    value: formatValue(
                      dataTrafficTicket?.details?.data_vencimento
                    ),
                  },
                  {
                    label: "Valor",
                    value: numberToCurrency(
                      dataTrafficTicket?.details?.valor_total
                    ),
                  },
                  {
                    label: "Descrição",
                    value: formatValue(dataTrafficTicket?.details?.descricao),
                  },
                  {
                    label: "Situação",
                    value: getStatusTrafficFine(
                      dataTrafficTicket?.status as
                        | TRAFFICFINE_STATUS
                        | undefined
                    ),
                  },
                ]}
              />
              {dataCharge ? (
                <TrafficFineSection
                  title="Cobrança"
                  className="mt-3 mb-5"
                  data={[
                    {
                      label: "Data",
                      value: formatValue(formatDate(dataCharge?.createdAt)),
                    },
                    {
                      label: "Vencimento",
                      value: formatValue(formatDate(dataCharge?.dueDate)),
                    },
                    {
                      label: "Situação",
                      value: getStatusCharge(
                        dataCharge?.status as CHARGE_STATUS | undefined
                      ),
                    },
                    {
                      label: "Link Fatura",
                      value: generateLinkInvoice(dataCharge.externalId!),
                    },
                  ]}
                />
              ) : (
                <div className="flex justify-end mt-2">
                  <ButtonWithLoader
                    isLoading={isLoadingGenerateCharge}
                    onClick={handleGenerateCharge}
                  >
                    Gerar Cobrança
                  </ButtonWithLoader>
                </div>
              )}

              {vehicleId && (
                <section className="tabs-item-style mt-3">
                  <div className="grid grid-cols-2">
                    <h4 className="font-semibold mb-2 text-2xl col-span-2">
                      Documentos de Infrações
                    </h4>
                  </div>
                  <div className="custom-file-input">
                    <ButtonWithLoader
                      type="button"
                      onClick={() => openPdfSelector(handleFileChange)}
                      disabled={isDisabled}
                      isLoading={isLoadingButton}
                    >
                      <span>
                        {!dataTrafficTicket?.attached ? `Incluir` : `Alterar`}{" "}
                        Documento de Infração
                      </span>
                    </ButtonWithLoader>
                  </div>
                  {dataTrafficTicket?.attached && (
                    <div className="mt-2 mb-2">
                      <a
                        target="_blank"
                        className="text-bluePrimary underline cursor-pointer"
                        rel="noopener noreferrer"
                        href={`https://assets.locavibe.com.br/${dataTrafficTicket.attached.toString()}`}
                      >
                        Visualizar
                      </a>
                    </div>
                  )}
                </section>
              )}
              <div className="div-tabs-border-line"></div>
              <section className="tabs-item-style">
                <div className="max-w-7xl mx-auto ">
                  <Button
                    variant="destructive"
                    className={`bg-redPrimary text-white font-semibold p-2 rounded-md mb-2 sm:mb-0`}
                    onClick={() => setIsLogModalOpen(true)}
                  >
                    Visualizar Logs
                  </Button>
                </div>
              </section>
            </>
          )}
        </div>
      </section>
      {isLogModalOpen && dataTrafficTicket && dataTrafficTicket._id && (
        <ModalViewLogs
          onClose={() => setIsLogModalOpen(false)}
          entity="trafficfines" // Entidade fixa como solicitado
          entityId={dataTrafficTicket._id} // ID do veículo do contexto
        />
      )}
    </>
  );
}
